/**
 * Upload Store
 * Manages file upload state, validation, and coordination with backend services
 *
 * Key Responsibilities:
 * - Track upload progress for multiple files
 * - Manage file metadata and validation
 * - Coordinate with AWS S3 for file storage
 * - Handle pod (file) and asset creation via API
 * - Persist upload state in localStorage for recovery
 * - Manage upload cancellation and error handling
 */

import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { useAuthStore } from './AuthStore.js';
import { useNodeStore } from './NodeStore.js';
import { usePodStore } from './PodStore.js';
import { uploadData, isCancelError } from 'aws-amplify/storage';
import Api from '../api/Api';
import { noop, set as _set, cloneDeep } from 'lodash';
import CreateManyPodsDuplicateUuidsError from '@/stores/error/CreateManyPodsDuplicateUuidsError.js';
import { validateRelativePath } from '@/components/utils/upload-validators.js';
import { analyzeUploadBatch, chunkFilesByRootFolder } from '@/components/utils/upload-chunking';
import { BackendLimits } from '@/components/constants/backend-limits';

/**
 * @typedef {Object} UploadFileMetadata
 * @property {string} filename - Original filename
 * @property {number} filesize - File size in bytes
 * @property {string} useruuid - UUID of user uploading
 * @property {string} [podid] - Pod ID if uploading as asset
 * @property {string} webkitRelativePath - Relative path for folder uploads
 * @property {string} nodeid - Target node ID
 * @property {string} contextNodeId - Context node ID for folder creation
 * @property {string} nodename - Target node name
 * @property {number} lastModified - File last modified timestamp
 * @property {string} fileuuid - Unique identifier for this upload
 * @property {string} [resourceId] - Backend resource ID after creation
 * @property {number} [assettype] - Asset type identifier
 */

/**
 * @typedef {Object} UploadFileLegal
 * @property {string} [categoryId] - Legal category ID
 * @property {string} [categoryName] - Custom category name
 * @property {string} [expiryDate] - Document expiry date
 */

/**
 * @typedef {Object} UploadFile
 * @property {File} file - Browser File object
 * @property {string} [podid] - Pod ID if uploading to pod
 * @property {number} status - Current upload status
 * @property {string} note - User-provided note
 * @property {boolean} teamonly - Team-only access flag
 * @property {string} errorMessage - Error message if failed
 * @property {object} errorMessageParameters - Parameters for error message i18n
 * @property {Function} cancelUpload - Function to cancel upload
 * @property {boolean} foundInLocalStorage - Whether recovered from localStorage
 * @property {{percentage: number, uploaded: number}} progress - Upload progress
 * @property {UploadFileMetadata} meta - File metadata
 * @property {UploadFileLegal} [legal] - Legal document metadata
 */

/**
 * Upload status constants
 * Represents the lifecycle of a file upload
 */
const UPLOAD_STATUSES = {
  ADDED: 1,        // File added to queue, not yet started
  WAITING: 2,      // Waiting in queue for upload
  UPLOADING: 3,    // Currently uploading
  FINISHED: 4,     // Successfully completed
  ERROR: 5,        // Failed with error
  CANCELLED: 6,    // Cancelled by user
};

/**
 * LocalStorage key for persisting file metadata
 * Allows recovery of uploads after browser refresh
 */
const STORAGE_KEY_FILES_METADATA = 'filesMetadata';

export const useUploadStore = defineStore('upload', {
  state: () => ({
    /** @type {UploadFile[]} Array of files in upload queue */
    files: [],
    blockedFiles: [],
    /** @type {object|null} Chunking progress tracking */
    chunkingProgress: null,
  }),

  getters: {
    /**
     * Gets upload status constants
     * @returns {object} Status constants
     */
    statuses: () => UPLOAD_STATUSES,
  },

  actions: {
    /**
     * Adds a file to the upload queue
     * Checks for duplicates based on UUID and file characteristics
     *
     * @param {UploadFile} file - File to add
     * @returns {{success: boolean, reason?: string, filename?: string}} Result object
     * @private
     */
    _addFile(file) {
      // Check for duplicate UUID
      const existingByUuid = this.files.findIndex((x) => x.meta.fileuuid === file.meta.fileuuid);
      if (existingByUuid >= 0) {
        return { success: false, reason: 'uuid_exists', filename: file.meta.filename };
      }

      // Check for duplicate file by characteristics
      // Use contextNodeId instead of nodeid because nodeid can change during upload
      // when folders are created, but contextNodeId remains constant
      const existingBySimilarity = this.files.find((existingFile) => {
        return existingFile.meta.filename === file.meta.filename
          && existingFile.meta.filesize === file.meta.filesize
          && existingFile.meta.webkitRelativePath === file.meta.webkitRelativePath
          && existingFile.meta.contextNodeId === file.meta.contextNodeId
          && existingFile.podid === file.podid; // Also check podid for asset uploads
      });

      if (existingBySimilarity) {
        return { success: false, reason: 'name_exists', filename: file.meta.filename };
      }

      this.files.push(file);
      return { success: true };
    },

    /**
     * Removes a file from the queue
     * @param {UploadFile} file - File to remove
     * @private
     */
    _deleteFile(file) {
      const index = this.files.indexOf(file);
      if (index >= 0) {
        this.files.splice(index, 1);
      }
    },

    /**
     * Resets the entire upload queue
     * Used when starting fresh or clearing all uploads
     * @private
     */
    _resetFiles() {
      this.files = [];
    },

    /**
     * ========================================
     * STATUS AND PROGRESS MANAGEMENT
     * ========================================
     */

    /**
     * Updates upload progress for a file
     * @param {{file: UploadFile, progress: object}} payload - File and progress data
     * @private
     */
    _updateProgress(payload) {
      const { file, progress } = payload;
      const index = this.files.indexOf(file);

      if (index >= 0) {
        this.files[index].progress.percentage = Math.round(
          (progress.transferredBytes / progress.totalBytes) * 100
        );
        this.files[index].progress.uploaded = progress.transferredBytes;
      } else {
        console.warn(`Could not update progress for file ${file.name} - file does not exist`);
      }
    },

    /**
     * Sets file status to WAITING
     * @param {UploadFile} file - File to update
     * @private
     */
    _setStatusWaiting(file) {
      const index = this.files.indexOf(file);
      if (index >= 0) {
        this.files[index].status = UPLOAD_STATUSES.WAITING;
      }
    },

    /**
     * Sets file status to UPLOADING
     * @param {UploadFile} file - File to update
     * @private
     */
    _setStatusUploading(file) {
      const index = this.files.indexOf(file);
      if (index >= 0) {
        this.files[index].status = UPLOAD_STATUSES.UPLOADING;
      }
    },

    /**
     * Sets file status to FINISHED
     * @param {UploadFile} file - File to update
     * @private
     */
    _setStatusFinished(file) {
      const index = this.files.indexOf(file);
      if (index >= 0) {
        this.files[index].status = UPLOAD_STATUSES.FINISHED;
      }
    },

    /**
     * Sets file status to ERROR with message
     * @param {{file: UploadFile, errorMessage: string, errorMessageParameters?: object}} payload
     * @private
     */
    _setStatusError(payload) {
      const index = this.files.indexOf(payload.file);
      if (index >= 0) {
        this.files[index].status = UPLOAD_STATUSES.ERROR;
        this.files[index].errorMessage = payload.errorMessage;
        this.files[index].errorMessageParameters = payload.errorMessageParameters || {};
      }
    },

    /**
     * Sets file status to CANCELLED
     * @param {UploadFile} file - File to update
     * @private
     */
    _setStatusCancelled(file) {
      const index = this.files.indexOf(file);
      if (index >= 0) {
        this.files[index].status = UPLOAD_STATUSES.CANCELLED;
      }
    },

    /**
     * ========================================
     * METADATA MANAGEMENT
     * ========================================
     */

    /**
     * Updates node ID for a file
     * Used when files are moved to different folders during upload
     * @param {{file: UploadFile, nodeid: string}} payload
     * @private
     */
    _setNodeID(payload) {
      const index = this.files.indexOf(payload.file);
      if (index >= 0) {
        this.files[index].meta.nodeid = payload.nodeid;
      }
    },

    /**
     * Updates pod ID for a file
     * Set after pod creation on backend
     * @param {{file: UploadFile, podid: string}} payload
     * @private
     */
    _setPodID(payload) {
      const index = this.files.indexOf(payload.file);
      if (index >= 0) {
        this.files[index].meta.podid = payload.podid;
      }
    },

    /**
     * Sets asset type for a file
     * @param {{fileuuid: string, value: number}} payload
     * @private
     */
    _setAssetType(payload) {
      const file = this.files.find((f) => f.meta.fileuuid === payload.fileuuid);
      if (file) {
        file.meta.assettype = payload.value;
      }
    },

    /**
     * Stores the cancel function for an upload
     * Allows cancelling uploads in progress
     * @param {{file: UploadFile, fn: Function}} payload
     * @private
     */
    _setCancelUploadFunction(payload) {
      const index = this.files.indexOf(payload.file);
      if (index >= 0) {
        this.files[index].cancelUpload = payload.fn;
      }
    },

    /**
     * Toggles team-only flag for a file
     * @param {UploadFile} file - File to toggle
     * @private
     */
    _toggleTeamonly(file) {
      const index = this.files.indexOf(file);
      if (index >= 0) {
        this.files[index].teamonly = !this.files[index].teamonly;
      }
    },

    /**
     * Sets note for a file
     * @param {{file: UploadFile, note: string}} payload
     * @private
     */
    _setNote(payload) {
      const index = this.files.indexOf(payload.file);
      if (index >= 0) {
        this.files[index].note = payload.note;
      }
    },

    /**
     * ========================================
     * LOCALSTORAGE PERSISTENCE
     * ========================================
     */

    /**
     * Removes file metadata from localStorage
     * Called after successful upload or cancellation
     * @param {UploadFile} file - File to remove
     * @private
     */
    _removeFileFromLocalstorage(file) {
      const filesMetadata = JSON.parse(localStorage.getItem(STORAGE_KEY_FILES_METADATA)) || [];
      const index = filesMetadata.findIndex((x) => x.fileuuid === file.meta.fileuuid);

      if (index >= 0) {
        filesMetadata.splice(index, 1);
        localStorage.setItem(STORAGE_KEY_FILES_METADATA, JSON.stringify(filesMetadata));
      }
    },

    /**
     * Saves file metadata to localStorage
     * Allows recovery after browser refresh
     * @param {UploadFileMetadata} metadata - Metadata to save
     * @private
     */
    _saveFileToLocalstorage(metadata) {
      const filesMetadata = JSON.parse(localStorage.getItem(STORAGE_KEY_FILES_METADATA)) || [];
      filesMetadata.push(metadata);
      localStorage.setItem(STORAGE_KEY_FILES_METADATA, JSON.stringify(filesMetadata));
    },

    /**
     * Updates file metadata in localStorage
     * @param {string} oldUuid - Old file UUID
     * @param {Partial<UploadFileMetadata>} updates - Updates to apply
     * @private
     */
    _updateFileInLocalstorage(oldUuid, updates) {
      const filesMetadata = JSON.parse(localStorage.getItem(STORAGE_KEY_FILES_METADATA)) || [];
      const fileMetadata = filesMetadata.find((x) => x.fileuuid === oldUuid);

      if (fileMetadata) {
        Object.assign(fileMetadata, updates);
        localStorage.setItem(STORAGE_KEY_FILES_METADATA, JSON.stringify(filesMetadata));
      }
    },

    /**
     * Adds multiple files to the upload queue
     * Handles duplicate detection and localStorage persistence
     *
     * @param {{nodeID: string, podID: string|null, fileList: File[]}} payload
     * @returns {{duplicateFiles: string[]}} Result with duplicate filenames
     */
    addFiles(payload) {
      const authStore = useAuthStore();

      const USERUUID = authStore.user.userId;
      const duplicateFiles = [];

      for (const browserFile of payload.fileList) {
        // Prepare file metadata
        const fileMeta = this._prepareFileMetadata({
          file: browserFile,
          nodeID: payload.nodeID,
          podID: payload.podID,
          useruuid: USERUUID,
        });

        // Check localStorage for existing upload
        const existingMetadata = this._findFileInLocalstorage(fileMeta);

        if (existingMetadata) {
          fileMeta.fileuuid = existingMetadata.fileuuid;
        } else {
          fileMeta.fileuuid = uuidv4();
          this._saveFileToLocalstorage(fileMeta);
        }

        // Create upload file object
        const uploadFile = {
          file: browserFile,
          podid: payload.podID,
          status: UPLOAD_STATUSES.ADDED,
          note: '',
          teamonly: false,
          errorMessage: '',
          errorMessageParameters: {},
          cancelUpload: noop,
          foundInLocalStorage: !!existingMetadata,
          progress: {
            percentage: 0,
            uploaded: 0,
          },
          meta: fileMeta,
        };

        // Add to queue
        const result = this._addFile(uploadFile);

        // Track duplicates (both UUID and name duplicates)
        if (!result.success && (result.reason === 'name_exists' || result.reason === 'uuid_exists')) {
          // Only add unique filenames to the duplicate list
          if (!duplicateFiles.includes(result.filename)) {
            duplicateFiles.push(result.filename);
          }
        }
      }

      return { duplicateFiles };
    },

    /**
     * Prepares file metadata from browser File object
     * @param {{file: File, nodeID: string, podID: string|null, useruuid: string}} params
     * @returns {UploadFileMetadata}
     * @private
     */
    _prepareFileMetadata(params) {
      const { file, nodeID, podID, useruuid } = params;
      const nodeStore = useNodeStore();

      let webkitRelativePath = file.webkitRelativePath || '';

      // Convert single filename to empty path to avoid creating subdirectory
      if (!webkitRelativePath || webkitRelativePath.indexOf('/') === -1) {
        webkitRelativePath = '';
      }

      return {
        filename: file.name,
        filesize: file.size,
        useruuid,
        podid: podID,
        webkitRelativePath,
        nodeid: nodeID,
        contextNodeId: nodeID,
        nodename: nodeStore.getNodeById(nodeID).name,
        lastModified: file?.lastModified || '',
      };
    },

    /**
     * Finds existing file metadata in localStorage
     * @param {UploadFileMetadata} fileMeta - Metadata to search for
     * @returns {UploadFileMetadata|null} Found metadata or null
     * @private
     */
    _findFileInLocalstorage(fileMeta) {
      const localStorageMetadata = JSON.parse(localStorage.getItem(STORAGE_KEY_FILES_METADATA)) || [];

      return localStorageMetadata.find((stored) => {
        return stored.filename === fileMeta.filename
          && stored.filesize === fileMeta.filesize
          && stored.useruuid === fileMeta.useruuid
          && stored.nodeid === fileMeta.nodeid
          && stored.contextNodeId === fileMeta.contextNodeId
          && stored.podid === fileMeta.podid
          && stored.nodename === fileMeta.nodename
          && stored.lastModified === fileMeta.lastModified
          && stored.webkitRelativePath === fileMeta.webkitRelativePath;
      }) || null;
    },

    /**
     * Removes a file from upload queue and localStorage
     * @param {UploadFile} file - File to remove
     */
    removeFile(file) {
      this._deleteFile(file);
      this._removeFileFromLocalstorage(file);
    },

    /**
     * Sets asset type for a file
     * @param {{fileuuid: string, value: number}} payload
     */
    setAssetType(payload) {
      this._setAssetType(payload);
    },

    /**
     * Toggles team-only access for a file
     * @param {UploadFile} file
     */
    toggleTeamonly(file) {
      this._toggleTeamonly(file);
    },

    /**
     * Sets note for a file
     * @param {{file: UploadFile, note: string}} payload
     */
    setNote(payload) {
      this._setNote(payload);
    },

    /**
     * Sets legal category ID for a file
     * @param {string} fileuuid - File UUID
     * @param {number} categoryId - Category ID
     */
    setLegalCategoryId(fileuuid, categoryId) {
      const file = this.files.find((f) => f.meta.fileuuid === fileuuid);
      if (file) {
        _set(file, 'legal.categoryId', categoryId);
      }
    },

    /**
     * Sets legal category custom name
     * @param {string} fileuuid - File UUID
     * @param {string} categoryName - Custom name
     */
    setLegalCategoryName(fileuuid, categoryName) {
      const file = this.files.find((f) => f.meta.fileuuid === fileuuid);
      if (file) {
        _set(file, 'legal.categoryName', categoryName);
      }
    },

    /**
     * Sets legal document expiry date
     * @param {string} fileuuid - File UUID
     * @param {Date} expiryDate - Expiry date
     */
    setLegalExpiryDate(fileuuid, expiryDate) {
      const file = this.files.find((f) => f.meta.fileuuid === fileuuid);
      if (file) {
        _set(file, 'legal.expiryDate', expiryDate);
      }
    },

    /**
     * ========================================
     * UPLOAD EXECUTION
     * ========================================
     */

    /**
     * Uploads all files in the queue
     * Determines whether to upload as new pods or as assets
     *
     * @param {{
     *   labels: Array,
     *   priorityProcessing: number,
     *   teamonlyForAll: number,
     *   disablePreview: number,
     *   requestId: string
     * }} payload - Upload configuration
     */
    async uploadAllFiles(payload) {
      const nodeStore = useNodeStore();
      const node = nodeStore.getCurrentNode;

      if (!node) {
        throw new Error('menus.uploadFiles.activeNodeRequired');
      }

      // Mark files as waiting and set node IDs
      const newWaitingFiles = [];
      for (const file of this.files) {
        if (file.status === UPLOAD_STATUSES.ADDED) {
          this._setStatusWaiting(file);

          if (!file.meta.nodeid) {
            this._setNodeID({ file, nodeid: node.id });
          }

          newWaitingFiles.push(file);
        }
      }

      // Set up page unload prevention
      this._preventPageUnload();

      // Get files that are actually waiting (excludes already processing files)
      const waitingFiles = this.files.filter(
        (x) => x.status === UPLOAD_STATUSES.WAITING && newWaitingFiles.includes(x)
      );

      // Determine upload type: assets vs new pods
      if (waitingFiles.every((v) => v.meta.podid)) {
        // Uploading as assets to existing pods
        for (const waitingFile of waitingFiles) {
          const waitingFilePayload = { ...payload, file: waitingFile };
          await this.uploadFile(waitingFilePayload);
        }
      } else {
        // Uploading as new pods
        await this._uploadManyFiles(
          {
            ...payload,
            nodeId: node.id,
          },
          waitingFiles
        );
      }

      // Remove page unload prevention
      this._removePageUnloadPrevention();
    },

    /**
     * Prevents page unload during uploads
     * @private
     */
    _preventPageUnload() {
      if (!window.onbeforeunload) {
        window.onbeforeunload = () => 'Leaving this page will cancel the current uploads. Are you sure?';
        window.timesUploading = 1;
      } else {
        window.timesUploading++;
      }
    },

    /**
     * Removes page unload prevention
     * @private
     */
    _removePageUnloadPrevention() {
      if (window.timesUploading === 1) {
        window.onbeforeunload = null;
        delete window.timesUploading;
      } else if (window.timesUploading > 1) {
        window.timesUploading--;
      }
    },

    /**
     * Uploads multiple files with hierarchical folder structure across multiple root nodes
     *
     * @param {object} payload - Upload configuration
     * @param {UploadFile[]} files - Files to upload
     * @private
     */
    async _uploadManyFiles(payload, files) {
      const nodeStore = useNodeStore();
      const podStore = usePodStore();
      const authStore = useAuthStore();

      const currentNode = nodeStore.getCurrentNode;

      /** @type {UploadFile[]} Files that pass validation */
      const uploadFiles = [];
      /** @type {{ id: string, file: UploadFile }[]} Files that need deletion */
      const deleteFiles = [];

      let totalSize = 0n;

      // Validate all files
      for (const file of files) {
        const validation = validateRelativePath(file.meta.webkitRelativePath);

        if (!validation.isValid) {
          this._setStatusError({
            file,
            errorMessage: validation.error,
            errorMessageParameters: validation.errorParams,
          });
        } else if (!currentNode || !file.meta.contextNodeId) {
          this._setStatusError({
            file,
            errorMessage: 'menus.uploadFiles.activeNodeRequired',
          });
        } else {
          uploadFiles.push(file);
          totalSize += BigInt(file.meta.filesize);
        }
      }

      if (uploadFiles.length === 0) {
        return;
      }

      // Check storage quota (use first root node for quota check)
      const firstRootNodeId = uploadFiles[0].meta.contextNodeId;
      const checkResult = await Api.service.checkResource('storage', totalSize, firstRootNodeId);
      if (checkResult?.data?.willExceedLimit) {
        for (const file of uploadFiles) {
          this._setStatusError({
            file,
            errorMessage: 'menus.uploadFiles.insufficientStorage',
          });
        }
        return;
      }

      // Check if chunking is needed
      const analysis = analyzeUploadBatch(uploadFiles);

      if (analysis.needsChunking) {
        console.log(
          `Chunking needed: ${analysis.totalFiles} files, ${analysis.uniqueFolders.size} folders`
        );
        return await this._uploadInChunks(uploadFiles, payload, authStore);
      }

      // Generate client temp IDs for tracking
      for (const file of uploadFiles) {
        if (!file.meta.resourceId) {
          const clientTempId = uuidv4();
          file.clientFileId = clientTempId;      // Client tracking ID (never changes)
          file.meta.fileuuid = null;           // Will be set to backend UUID after pod creation
        }
      }

      // Prepare hierarchical payload with roots
      const preparedPodsPayload = this._prepareHierarchicalPayload(uploadFiles, authStore, payload);

      let createdPods = [];
      let createPodRetries = 10;

      // Create all pods in a single request with retry logic
      while (createPodRetries > 0) {
        try {
          createdPods = await podStore.createManyPods(preparedPodsPayload);
          createPodRetries = 0;
        } catch (error) {
          console.error('Error creating pods:', error);

          if (error instanceof CreateManyPodsDuplicateUuidsError) {
            // Handle UUID conflicts by regenerating
            await this._handleDuplicateUuidErrorForBulk(error, preparedPodsPayload, uploadFiles);

            if (--createPodRetries === 0) {
              this._markFilesAsErrored(uploadFiles, error.duplicateUuids);
              return; // Don't proceed to S3 upload after max retries
            }
          } else {
            this._markAllFilesAsErrored(uploadFiles, createdPods, error);
            // eslint-disable-next-line no-useless-assignment
            createPodRetries = 0;
            return; // Don't proceed to S3 upload on error
          }
        }
      }

      // Update files with created pod IDs
      for (const createdPod of createdPods) {
        const file = uploadFiles.find((v) => v.clientFileId === createdPod.clientFileId);
        if (file) {
          this._setPodID({ file, podid: createdPod.id });
        }
      }

      // Map backend-generated UUIDs to files for S3 upload
      for (const createdPod of createdPods) {
        const file = uploadFiles.find((v) => v.clientFileId === createdPod.clientFileId);
        if (file) {
          console.log(`Mapping UUID: clientTempId=${file.clientFileId} → backendUuid=${createdPod.uuid}`);
          file.meta.fileuuid = createdPod.uuid; // Set backend UUID for S3
        }
      }

      // Update localStorage with resource IDs
      await this._updateLocalStorageWithResourceIds(uploadFiles, createdPods, deleteFiles);

      // Delete any obsolete pods
      if (deleteFiles.length > 0) {
        await podStore.deletePods(deleteFiles.map((v) => v.id));
        for (const fileToDelete of deleteFiles) {
          this._removeFileFromLocalstorage(fileToDelete.file);
        }
      }

      // Upload files to S3
      for (const file of uploadFiles) {
        if (file.status === UPLOAD_STATUSES.CANCELLED || file.status === UPLOAD_STATUSES.ERROR) {
          continue;
        }

        await this._uploadFileToS3(file, payload);
      }
    },
    /**
     * Builds hierarchical structure grouped by root nodes
     * Each root node can have its own folders and files
     *
     * @param {UploadFile[]} uploadFiles - Files to process
     * @param {object} authStore - Auth store
     * @param {object} payload - Upload payload
     * @returns {object} Payload for createManyPods with roots array
     * @private
     */
    _prepareHierarchicalPayload(uploadFiles, authStore, payload, createdFolderMap = new Map()) {
      // Group files by their root node (contextNodeId)
      const rootsMap = new Map();

      for (const file of uploadFiles) {
        const rootNodeId = file.meta.contextNodeId;

        if (!rootsMap.has(rootNodeId)) {
          rootsMap.set(rootNodeId, {
            nodeid: rootNodeId,
            nodesMap: new Map(),
            pods: [],
          });
        }

        const root = rootsMap.get(rootNodeId);
        const relativePath = file.meta.webkitRelativePath;
        const podData = this._prepareFileForPodCreation(file);

        // File without folder path - goes to root pods array
        if (!relativePath || relativePath.indexOf('/') === -1) {
          root.pods.push(podData);
          continue;
        }

        // Extract folder path (remove filename)
        const pathSegments = relativePath.split('/');
        pathSegments.pop(); // Remove filename
        const folderPath = pathSegments.join('/');

        // Get or create node entry for this folder
        if (!root.nodesMap.has(folderPath)) {
          const mapKey = `${rootNodeId}:${folderPath}`;
          if (createdFolderMap.has(mapKey)) {
            // Folder created in a previous chunk — reference by ID
            root.nodesMap.set(folderPath, {
              id: createdFolderMap.get(mapKey),
              pods: [],
            });
          } else {
            // New folder — use relativePath for backend to create it
            root.nodesMap.set(folderPath, {
              relativePath: folderPath,
              pods: [],
            });
          }
        }

        // Add file to its folder
        root.nodesMap.get(folderPath).pods.push(podData);
      }

      // Convert maps to arrays for each root
      const roots = [];

      for (const rootData of rootsMap.values()) {
        const rootObject = {
          id: rootData.nodeid,
          nodes: Array.from(rootData.nodesMap.values()),
          pods: rootData.pods,
        };

        roots.push(rootObject);
      }

      // Build final payload
      return {
        labels: payload.labels,
        useruuid: authStore.user.userId,
        teamonly: payload.teamonlyForAll,
        roots,
      };
    },

    /**
     * Handles duplicate UUID errors for bulk operations with roots
     * Regenerates UUIDs for duplicated pods in hierarchical structure
     *
     * @param {CreateManyPodsDuplicateUuidsError} error - Error object
     * @param {object} preparedPodsPayload - Hierarchical payload with roots
     * @param {UploadFile[]} uploadFiles - Upload files
     * @private
     */
    async _handleDuplicateUuidErrorForBulk(error, preparedPodsPayload, uploadFiles) {
      const localStorageMetadata = JSON.parse(localStorage.getItem(STORAGE_KEY_FILES_METADATA)) || [];

      // Update UUIDs in each root
      for (const root of preparedPodsPayload.roots) {
        // Update UUIDs in nodes array
        if (root.nodes) {
          for (const node of root.nodes) {
            const duplicatedPods = node.pods.filter((p) => error.duplicateUuids.includes(p.uuid));

            for (const pod of duplicatedPods) {
              const oldUuid = pod.uuid;
              const newUuid = uuidv4();

              // Update in localStorage
              for (const storedMetadata of localStorageMetadata) {
                if (storedMetadata.fileuuid === oldUuid) {
                  storedMetadata.fileuuid = newUuid;
                }
              }

              // Update in pods payload
              pod.uuid = newUuid;

              // Update in upload files
              const uploadFile = uploadFiles.find((f) => f.meta.fileuuid === oldUuid);
              if (uploadFile) {
                uploadFile.meta.fileuuid = newUuid;
              }
            }
          }
        }

        // Update UUIDs in root pods array
        if (root.pods) {
          const duplicatedPods = root.pods.filter((p) => error.duplicateUuids.includes(p.uuid));

          for (const pod of duplicatedPods) {
            const oldUuid = pod.uuid;
            const newUuid = uuidv4();

            // Update in localStorage
            for (const storedMetadata of localStorageMetadata) {
              if (storedMetadata.fileuuid === oldUuid) {
                storedMetadata.fileuuid = newUuid;
              }
            }

            // Update in pods payload
            pod.uuid = newUuid;

            // Update in upload files
            const uploadFile = uploadFiles.find((f) => f.meta.fileuuid === oldUuid);
            if (uploadFile) {
              uploadFile.meta.fileuuid = newUuid;
            }
          }
        }
      }

      localStorage.setItem(STORAGE_KEY_FILES_METADATA, JSON.stringify(localStorageMetadata));
    },

    /**
     * Marks files as errored based on duplicate UUIDs
     * @param {UploadFile[]} uploadFiles - Files to check
     * @param {string[]} duplicateUuids - Duplicate UUIDs
     * @private
     */
    _markFilesAsErrored(uploadFiles, duplicateUuids) {
      const problematicFiles = uploadFiles.filter((file) => duplicateUuids.includes(file.meta.fileuuid));

      for (const file of problematicFiles) {
        this._setStatusError({
          file,
          errorMessage: 'Could not upload the file',
        });
      }
    },

    /**
     * Marks all files as errored after pod creation failure
     * @param {UploadFile[]} uploadFiles - Files to mark
     * @param {Array} createdPodResults - Successfully created pods
     * @param {Error} error - Error that occurred
     * @private
     */
    _markAllFilesAsErrored(uploadFiles, createdPodResults, error) {
      const createdPodIds = createdPodResults?.map((v) => v.uuid) || [];

      // Extract meaningful error info from API response
      let errorMessage = error.message || 'Unknown error';
      const apiError = error.response?.data?.error;

      if (apiError) {
        const code = apiError.code;
        if (typeof code === 'string' && code.startsWith('ERR')) {
          errorMessage = `backend.${code}`;
        } else if (apiError.message) {
          errorMessage = apiError.message;
        }
      }

      for (const file of uploadFiles) {
        if (!createdPodIds.includes(file.meta.fileuuid)) {
          this._setStatusError({
            file,
            errorMessage,
          });
        }
      }
    },

    /**
     * Uploads files in chunks when they exceed backend limits
     * @param {UploadFile[]} files - Files to upload
     * @param {object} payload - Upload payload
     * @param {object} authStore - Auth store
     * @private
     */
    async _uploadInChunks(files, payload, authStore) {
      const chunks = chunkFilesByRootFolder(files, BackendLimits);
      const totalChunks = chunks.length;

      console.log(`Uploading in ${totalChunks} chunks`);

      // Track progress
      this.chunkingProgress = {
        currentChunk: 0,
        totalChunks,
        isChunking: true,
      };

      const allResults = [];
      const createdFolderMap = new Map(); // rootNodeId:folderPath → folderId

      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];

        // Stop processing chunks if all files have been cancelled
        const allCancelled = chunk.files.every(
          (f) => f.status === UPLOAD_STATUSES.CANCELLED
        );
        if (allCancelled) {
          console.log(`Chunk ${i + 1} skipped: all files cancelled`);
          break;
        }

        this.chunkingProgress.currentChunk = i + 1;

        console.log(`Processing chunk ${i + 1}/${totalChunks} (${chunk.files.length} files)`);

        try {
          const result = await this._processChunk(chunk.files, payload, authStore, createdFolderMap);
          allResults.push(...result);
        } catch (error) {
          console.error(`Chunk ${i + 1} failed:`, error);
          // Continue with next chunk instead of aborting
          // Files in failed chunk are already marked as errored
        }
      }

      this.chunkingProgress.isChunking = false;
      return allResults;
    },

    /**
     * Processes a single chunk of files (reuses _uploadManyFiles logic)
     * @param {UploadFile[]} files - Files to process
     * @param {object} payload - Upload payload
     * @param {object} authStore - Auth store
     * @returns {Promise<Array>} Created pods
     * @private
     */
    async _processChunk(files, payload, authStore, createdFolderMap = new Map()) {
      // Early exit if all files in this chunk have been cancelled
      const allCancelled = files.every(
        (f) => f.status === UPLOAD_STATUSES.CANCELLED
      );
      if (allCancelled) {
        return [];
      }

      const podStore = usePodStore();

      /** @type {{ id: string, file: UploadFile }[]} Files that need deletion */
      const deleteFiles = [];

      // Generate client temp IDs for tracking
      for (const file of files) {
        if (!file.meta.resourceId) {
          const clientTempId = uuidv4();
          file.clientFileId = clientTempId;
          file.meta.fileuuid = null;
        }
      }

      // Prepare hierarchical payload
      const preparedPodsPayload = this._prepareHierarchicalPayload(files, authStore, payload, createdFolderMap);

      let createdPods = [];
      let createPodRetries = 10;

      // Create all pods in a single request with retry logic
      while (createPodRetries > 0) {
        try {
          createdPods = await podStore.createManyPods(preparedPodsPayload);
          createPodRetries = 0;
        } catch (error) {
          console.error('Error creating pods:', error);

          if (error instanceof CreateManyPodsDuplicateUuidsError) {
            await this._handleDuplicateUuidErrorForBulk(error, preparedPodsPayload, files);

            if (--createPodRetries === 0) {
              this._markFilesAsErrored(files, error.duplicateUuids);
              return [];
            }
          } else {
            this._markAllFilesAsErrored(files, createdPods, error);
            // eslint-disable-next-line no-useless-assignment
            createPodRetries = 0;
            return [];
          }
        }
      }

      // Update files with created pod IDs
      for (const createdPod of createdPods) {
        const file = files.find((v) => v.clientFileId === createdPod.clientFileId);
        if (file) {
          this._setPodID({ file, podid: createdPod.id });
        }
      }

      // Map backend-generated UUIDs to files for S3 upload
      for (const createdPod of createdPods) {
        const file = files.find((v) => v.clientFileId === createdPod.clientFileId);
        if (file) {
          console.log(`Mapping UUID: clientTempId=${file.clientFileId} → backendUuid=${createdPod.uuid}`);
          file.meta.fileuuid = createdPod.uuid;
        }
      }

      // Update createdFolderMap with folder IDs from this chunk's response
      for (const createdPod of createdPods) {
        const file = files.find((v) => v.clientFileId === createdPod.clientFileId);
        if (file && file.meta.webkitRelativePath && file.meta.webkitRelativePath.includes('/')) {
          const pathSegments = file.meta.webkitRelativePath.split('/');
          pathSegments.pop();
          const folderPath = pathSegments.join('/');
          const mapKey = `${file.meta.contextNodeId}:${folderPath}`;
          if (!createdFolderMap.has(mapKey)) {
            createdFolderMap.set(mapKey, createdPod.nodeid);
          }
        }
      }

      // Update localStorage with resource IDs
      await this._updateLocalStorageWithResourceIds(files, createdPods, deleteFiles);

      // Delete any obsolete pods
      if (deleteFiles.length > 0) {
        await podStore.deletePods(deleteFiles.map((v) => v.id));
        for (const fileToDelete of deleteFiles) {
          this._removeFileFromLocalstorage(fileToDelete.file);
        }
      }

      // Upload files to S3
      for (const file of files) {
        if (file.status === UPLOAD_STATUSES.CANCELLED || file.status === UPLOAD_STATUSES.ERROR) {
          continue;
        }

        await this._uploadFileToS3(file, payload);
      }

      return createdPods;
    },

    /**
     * Updates localStorage with resource IDs after pod creation
     * @param {UploadFile[]} uploadFiles - Upload files
     * @param {Array} createdPods - Created pods
     * @param {Array} deleteFiles - Files to mark for deletion
     * @private
     */
    async _updateLocalStorageWithResourceIds(uploadFiles, createdPods, deleteFiles) {
      const localStorageMetadata = JSON.parse(localStorage.getItem(STORAGE_KEY_FILES_METADATA)) || [];

      for (const storedMetadata of localStorageMetadata) {
        const uploadFileIdx = uploadFiles.findIndex((v) => v.meta.fileuuid === storedMetadata.fileuuid);

        if (uploadFileIdx === -1) {
          continue;
        }

        if (storedMetadata.resourceId) {
          deleteFiles.push({
            id: storedMetadata.resourceId,
            file: uploadFiles[uploadFileIdx],
          });
        } else {
          const createdPod = createdPods.find((v) => v.clientFileId === uploadFiles[uploadFileIdx].clientFileId);
          if (createdPod) {
            storedMetadata.resourceId = createdPod.id;
          }
        }
      }

      localStorage.setItem(STORAGE_KEY_FILES_METADATA, JSON.stringify(localStorageMetadata));
    },

    /**
     * Prepares file data for pod creation API call
     * @param {UploadFile} file - File to prepare
     * @returns {object} Pod data
     * @private
     */
    _prepareFileForPodCreation(file) {
      const podData = {
        clientFileId: file.clientFileId,  // Use dedicated clientFileId field
        filename: file.meta.filename,
        // filesize: file.meta.filesize,
        // nodeid: file.meta.nodeid || null,
        // relativePath: file.meta.webkitRelativePath,
      };

      if (file.note && file.note.trim() !== '') {
        podData.note = file.note;
      }

      if (file.meta.lastModified) {
        podData.lastModified = file.meta.lastModified.toString();
      }

      return podData;
    },

    /**
     * Uploads a file to S3
     * @param {UploadFile} file - File to upload
     * @param {object} payload - Upload configuration
     * @private
     */
    async _uploadFileToS3(file, payload) {
      const options = {
        metadata: cloneDeep(file.meta),
        onProgress: (progress) => {
          this._updateProgress({ file, progress });
        },
      };

      // Encode metadata for S3
      options.metadata.filesize = file.meta.filesize.toString();
      options.metadata.filename = encodeURIComponent(file.meta.filename);
      options.metadata.nodename = encodeURIComponent(file.meta.nodename);
      options.metadata.webkitRelativePath = encodeURIComponent(file.meta.webkitRelativePath);
      options.metadata.priority = payload.priorityProcessing ? 1 : 0;
      options.metadata.disablePreview = payload.disablePreview ? 1 : 0;
      options.metadata.checkauthenticity = payload.checkAuthenticity ? 1 : 0;
      options.metadata.nodeid = file.meta.nodeid;

      try {
        this._setStatusUploading(file);

        const operation = await uploadData({
          path: `public/${file.meta.fileuuid}`,
          data: file.file,
          options,
        });

        this._setCancelUploadFunction({ file, fn: operation.cancel });
        await operation.result;

        if (file.status !== UPLOAD_STATUSES.CANCELLED) {
          this._setStatusFinished(file);
        }

        this._removeFileFromLocalstorage(file);
      } catch (err) {
        if (isCancelError(err)) {
          return;
        }

        console.error('Error uploading file:', err);
        this._setStatusError({
          file,
          errorMessage: typeof err === 'string' ? err : '',
        });
      }
    },

    /**
     * ========================================
     * SINGLE FILE UPLOAD (LEGACY/ASSETS)
     * ========================================
     */

    /**
     * Uploads a single file (legacy method for assets)
     * @param {object} waitingFilePayload - File and upload config
     * @deprecated Use _uploadManyFiles for better performance
     */
    async uploadFile(waitingFilePayload) {
      const {
        file,
        labels,
        priorityProcessing,
        teamonlyForAll,
        requestId,
        disablePreview,
        checkAuthenticity
      } = waitingFilePayload;

      if (file.status === UPLOAD_STATUSES.CANCELLED) {
        return;
      }

      // Validate relative path
      const validation = validateRelativePath(file.meta.webkitRelativePath);
      if (!validation.isValid) {
        this._setStatusError({
          file,
          errorMessage: validation.error,
          errorMessageParameters: validation.errorParams,
        });
        return;
      }

      this._setStatusUploading(file);

      const nodeStore = useNodeStore();
      const node = nodeStore.getCurrentNode;

      try {
        if (!node && !file.meta.nodeid) {
          throw new Error('menus.uploadFiles.activeNodeRequired');
        }

        if (!file.meta.nodeid) {
          this._setNodeID({ file, nodeid: node.id });
        }

        // Prepare pod data
        const podData = {
          uuid: file.meta.fileuuid,
          filename: file.meta.filename,
          filesize: file.meta.filesize,
          useruuid: file.meta.useruuid,
          teamonly: teamonlyForAll ? 1 : 0,
          note: file.note,
          nodeid: file.meta.nodeid,
          webkitRelativePath: file.meta.webkitRelativePath,
          labels,
          requestId,
          disablePreview: disablePreview ? 1 : 0,
          checkauthenticity: checkAuthenticity ? 1 : 0,
        };

        if (file.meta.lastModified) {
          podData.lastModified = file.meta.lastModified.toString();
        }

        const nodeId = file.meta.nodeid;
        const podStore = usePodStore();

        // Determine API verb (create pod vs create asset)
        let VERB = podStore.createPod;

        if (file.meta.podid) {
          podData.podid = file.meta.podid;
          delete podData.nodeid;
          delete podData.teamonly;
          delete podData.note;
          delete file.meta.nodeid;

          VERB = podStore.createPodAsset;

          if (file.meta.assettype) {
            podData.assettype = file.meta.assettype;
          }
          if (file.legal?.categoryId) {
            podData.legalCategoryId = file.legal.categoryId;
          }
          if (file.legal?.categoryName) {
            podData.legalCategoryName = file.legal.categoryName;
          }
          if (file.legal?.expiryDate) {
            podData.legalExpiryDate = file.legal.expiryDate;
          }
        }

        // Check storage quota
        const res = await Api.service.checkResource('storage', podData.filesize, nodeId);
        if (res?.data?.willExceedLimit) {
          throw new Error('menus.uploadFiles.insufficientStorage');
        }

        // Create pod/asset
        const createResult = await VERB(podData);

        // Handle cancellation
        const fileStillExists = this.files.indexOf(file) >= 0;
        if (VERB === podStore.createPod && !fileStillExists) {
          await podStore.deletePods([createResult.id]);
          this._removeFileFromLocalstorage(file);
          return;
        }

        if (VERB === podStore.createPod && fileStillExists) {
          this._setPodID({ file, podid: createResult.id });
        }

        // Upload to S3
        await this._uploadFileToS3(file, { priorityProcessing, disablePreview: 0 });
      } catch (e) {
        if (isCancelError(e)) {
          return;
        }

        console.error('Error uploading file:', e);
        this._setStatusError({
          file,
          errorMessage: typeof e === 'string' ? e : e.message || '',
        });
      }
    },

    /**
     * ========================================
     * UPLOAD CANCELLATION
     * ========================================
     */

    /**
     * Cancels a specific upload
     * @param {UploadFile} file - File to cancel
     */
    async cancelUpload(file) {
      const index = this.files.indexOf(file);

      if (index >= 0) {
        const record = this.files[index];
        this._removeFileFromLocalstorage(record);

        if (record.status === UPLOAD_STATUSES.UPLOADING || record.status === UPLOAD_STATUSES.WAITING) {
          await record.cancelUpload();

          if (record.meta.podid) {
            const podStore = usePodStore();
            await podStore.deletePods([record.meta.podid], true);
          }
        }

        this._setStatusCancelled(record);
      }
    },

    /**
     * Cancels all ongoing uploads
     */
    async cancelUploads() {
      const cancelUploadOfFiles = this.files.filter(
        (file) => file.status !== UPLOAD_STATUSES.FINISHED && file.status !== UPLOAD_STATUSES.ERROR
      );

      if (cancelUploadOfFiles.length === 0) {
        return;
      }

      // Mark all as cancelled and clean up localStorage
      for (const file of cancelUploadOfFiles) {
        this._setStatusCancelled(file);
        this._removeFileFromLocalstorage(file);
      }

      // Cancel all uploads
      await Promise.all(cancelUploadOfFiles.map((file) => file.cancelUpload()));

      // Delete newly created pods
      const podStore = usePodStore();
      const newlyCreatedPodIds = cancelUploadOfFiles
        .filter((file) => !file.podid && file.meta.podid !== null)
        .map((file) => file.meta.podid);

      if (newlyCreatedPodIds.length > 0) {
        await podStore.deletePods(newlyCreatedPodIds, true);
      }

      window.onbeforeunload = null;
    },
    setBlockedFiles: function(podIds) {
      if (!Array.isArray(podIds)) {
        throw new Error('podIds must be an array.');
      }

      this.blockedFiles = podIds;
    }
  },
});
